import { Box } from "@itandi/itandi-bb-ui/Box";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useSWRConfig } from "swr";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { SurveyModal } from "~/components/common/survey/SurveyModal";
import { useDealCompletionReportImages } from "~/hooks/deals/useDealCompletionReportImages";
import { useDealEstimationHeaders } from "~/hooks/deals/useDealEstimationHeaders";
import { useDealEstimationHistories } from "~/hooks/deals/useDealEstimationHistories";
import { useDealEstimations } from "~/hooks/deals/useDealEstimations";
import { useDealInspectionReportImages } from "~/hooks/deals/useDealInspectionReportImages";
import { useDealSurveys } from "~/hooks/deals/useDealSurveys";
import useItandiAccount from "~/hooks/useItandiAccount";
import useLoader from "~/hooks/useLoader";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { propertyDealCacheKey } from "~/hooks/usePropertyDeals";
import type { DealWithPrices } from "~/models/Deal";
import {
	AWAITING_ACCEPTANCE,
	FINISHED,
	IN_CONSTRUCTION,
	IN_ESTIMATION,
	NOW_ACCEPTING,
	OWN_AWAITING_ACCEPTANCE,
	OWN_FINISHED,
	OWN_IN_CONSTRUCTION,
	OWN_IN_ESTIMATION,
	OWN_SEND_ESTIMATE,
	SEND_ESTIMATE
} from "~/models/Status";
import type { SurveyAnswer } from "~/models/Survey";
import { isSuccess } from "~/utils/api/api_handler";
import { update as updateStatus } from "~/utils/api/DealStatus";
import type { StatusForwardDeps } from "~/utils/ForwardStatusValidationUtil";
import { forwardStatusValidation } from "~/utils/ForwardStatusValidationUtil";

type Props = {
	deal: DealWithPrices;
	propertyDealId: number;
};

export const ForwardStatus: React.FC<Props> = ({
	deal,
	propertyDealId
}: Props) => {
	const { notify } = useNotification();
	const { Loader, showLoader, hideLoader } = useLoader();
	const { data: account } = useItandiAccount().currentAccount();

	// FIXME: HookがHookを返すのをやめたい
	const useDealEstimationsApi = useDealEstimations();
	const useDealEstimationHistoriesApi = useDealEstimationHistories();
	const useInspectionImagesApi = useDealInspectionReportImages();
	const useCompletionImagesApi = useDealCompletionReportImages();
	const useEstimationHeadersApi = useDealEstimationHeaders();
	const useSurveysApi = useDealSurveys();

	const { mutate: globalMutate } = useSWRConfig();
	const openSurveyHandler = useOpenHandler();
	const openConfirmHandler = useOpenHandler();

	const getNextStatusId = (): number | null => {
		const withVendorStatuses = [
			IN_ESTIMATION,
			SEND_ESTIMATE,
			IN_CONSTRUCTION,
			AWAITING_ACCEPTANCE,
			FINISHED
		];
		const ownStatuses = [
			OWN_IN_ESTIMATION,
			OWN_SEND_ESTIMATE,
			OWN_IN_CONSTRUCTION,
			OWN_AWAITING_ACCEPTANCE,
			OWN_FINISHED
		];

		// 施工会社未選択時は IN_ESTIMATION と OWN_IN_ESTIMATION の判定ができないのでnull返す
		if (deal.dealVendorCompanies.length === 0) return null;
		const isOwn = deal.dealVendorCompanies[0].vendorCompany === null;

		if (deal.statusId === NOW_ACCEPTING) {
			return isOwn ? OWN_IN_ESTIMATION : IN_ESTIMATION;
		}

		const statuses = isOwn ? ownStatuses : withVendorStatuses;
		const currentIndex = statuses.indexOf(deal.statusId);
		return statuses[currentIndex + 1];
	};

	const nextStatusId = getNextStatusId();

	const needRelatedEstimationsFor = (statusId: number): boolean =>
		[IN_ESTIMATION, SEND_ESTIMATE, IN_CONSTRUCTION].includes(statusId);
	const needCompletionImagesFor = (statusId: number): boolean =>
		statusId === IN_CONSTRUCTION;
	const needEstimationHeadersFor = (statusId: number): boolean =>
		[SEND_ESTIMATE, IN_CONSTRUCTION].includes(statusId);

	// 取得が必要なデータの判定
	const needRelatedEstimations = needRelatedEstimationsFor(deal.statusId);
	const needCompletionImages = needCompletionImagesFor(deal.statusId);
	const needEstimationHeaders = needEstimationHeadersFor(deal.statusId);

	// 各種データの取得
	const dealEstimations = useDealEstimationsApi.index(
		needRelatedEstimations ? deal.id : null
	);
	const dealEstimationHistories = useDealEstimationHistoriesApi.index(
		needRelatedEstimations ? deal.id : null
	);
	const inspectionImages = useInspectionImagesApi.index(
		needRelatedEstimations ? deal.id : null
	);
	const completionImages = useCompletionImagesApi.index(
		needCompletionImages ? deal.id : null
	);
	const estimationHeaders = useEstimationHeadersApi.index(
		needEstimationHeaders ? deal.id : null
	);

	// バリデーション用データ構築
	const statusForwardData: StatusForwardDeps = {
		completionImages: completionImages.data,
		dealEstimationHistories: dealEstimationHistories.data,
		dealEstimations: dealEstimations.data,
		estimationHeaders: estimationHeaders.data,
		inspectionImages: inspectionImages.data
	};

	const validationDataLoaded =
		(!needRelatedEstimations ||
			(dealEstimations.data &&
				dealEstimationHistories.data &&
				inspectionImages.data)) &&
		(!needCompletionImages || completionImages.data) &&
		(!needEstimationHeaders || estimationHeaders.data);
	const canForwardStatus = forwardStatusValidation(
		deal,
		account,
		statusForwardData
	);
	const isForwardButtonEnabled =
		account && validationDataLoaded && canForwardStatus;

	// アンケート関連
	const needSurveys = nextStatusId === FINISHED;
	const { data: surveys } = useSurveysApi.index(needSurveys ? deal.id : null);
	const surveyPresent = needSurveys && !!surveys?.length && !account?.isAdmin;

	const {
		isOpen: openSurveyConfirm,
		handleOpen: handleOpenSurveyConfirm,
		handleClose: handleCloseSurveyConfirm
	} = openSurveyHandler;
	const {
		isOpen: openConfirm,
		handleOpen: handleOpenConfirm,
		handleClose: handleCloseConfirm
	} = openConfirmHandler;

	const refetchStatusRelatedData = (statusId: number): void => {
		if (needRelatedEstimationsFor(statusId)) {
			dealEstimations.mutate();
			dealEstimationHistories.mutate();
			inspectionImages.mutate();
		}
		if (needCompletionImagesFor(statusId)) {
			completionImages.mutate();
		}
		if (needEstimationHeadersFor(statusId)) {
			estimationHeaders.mutate();
		}
	};

	const handleForwardStatus = async (
		surveyAnswers: SurveyAnswer[] | null
	): Promise<void> => {
		if (nextStatusId === null) return;

		showLoader();
		try {
			const result = await updateStatus(
				deal.id,
				nextStatusId,
				null,
				surveyAnswers
			);
			if (!isSuccess(result)) {
				notify({
					content: result.message || "エラーが発生しました。",
					variant: "error"
				});
				return;
			}
			await globalMutate(propertyDealCacheKey(propertyDealId));
			refetchStatusRelatedData(nextStatusId);
			notify({ content: "更新しました。", variant: "success" });
		} finally {
			hideLoader();
		}
	};
	const handleAcceptForward = async (): Promise<void> => {
		await handleForwardStatus(null);
		handleCloseConfirm();
	};
	const handleAcceptForwardWithSurvey = async (
		surveyAnswers: SurveyAnswer[]
	): Promise<void> => {
		await handleForwardStatus(surveyAnswers);
		handleCloseSurveyConfirm();
	};

	return (
		<>
			<Loader />
			<Box marginTop={5}>
				<Button
					disabled={!isForwardButtonEnabled}
					height="small"
					onClick={surveyPresent ? handleOpenSurveyConfirm : handleOpenConfirm}
					type="button"
				>
					進める
				</Button>
			</Box>
			<ConfirmModal
				actionText="確定"
				confirmType="save"
				handleAccept={handleAcceptForward}
				handleCancel={handleCloseConfirm}
				isOpen={openConfirm}
			>
				ステータスを進めます。
			</ConfirmModal>
			{surveyPresent ? (
				<SurveyModal
					handleAccept={handleAcceptForwardWithSurvey}
					handleClose={handleCloseSurveyConfirm}
					isOpen={openSurveyConfirm}
					surveys={surveys ?? []}
				/>
			) : null}
		</>
	);
};
